// V3 basket: the drawer on every page and, with data-mode="page", the page at /cart.
//
// Shopify renders the drawer and re-renders it after every change, so prices,
// discounts, delivery and add-ons are always Shopify's. A reply takes around
// 400 ms, so the drawer does not wait for it: a tap updates the quantity, the
// totals and the free-delivery bar at once from what is on screen, the request
// goes in the background, and Shopify's markup is then merged in without
// replacing nodes, so nothing flickers and focus stays put.
if (!customElements.get('eg-cart-drawer')) {
  customElements.define('eg-cart-drawer', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.root = window.Shopify?.routes?.root || '/';
      this.ops = [];         // changes not yet sent, in order
      this.arriving = [];    // add-ons on their way: { price, quantity }
      this.sent = new Map(); // row id -> quantity already sent and not yet merged in
      this.server = null;    // the latest markup Shopify sent, where row keys are read from

      // Product forms ask which sections to re-render just before they send an add.
      // Answering puts this drawer in the same reply; hearing the question at all
      // means an add has started, so the drawer opens straight away.
      document.addEventListener('cart:grouped-sections', event => {
        event.detail?.sections?.push(this.sectionId);
        if (!this.asking) this.beginAdd();
      }, options);
      document.addEventListener('submit', event => { if (event.target.matches?.('form[action*="/cart/add"]')) this.submitted = { form: event.target, at: Date.now() }; }, { ...options, capture: true });
      document.addEventListener('click', event => this.onPageClick(event), options);
      document.addEventListener('product-ajax:added', event => this.onAdded(event), options);
      document.addEventListener('product-ajax:error', () => this.cancelAdd(), options);
      document.addEventListener('shopify:section:select', event => { if (event.detail?.sectionId === this.sectionId) this.open(); }, options);
      document.addEventListener('shopify:section:deselect', event => { if (event.detail?.sectionId === this.sectionId) this.close(); }, options);
      window.addEventListener('pageshow', event => { if (event.persisted) { this.querySelector('[data-loading]')?.removeAttribute('data-loading'); this.refresh(); } }, options);
      this.addEventListener('click', event => this.onClick(event), options);
      this.addEventListener('change', event => this.onChange(event), options);
      this.addEventListener('keydown', event => this.onKeydown(event), options);

      const events = window.MinimogEvents, topics = window.MinimogTheme?.pubSubEvents;
      if (events?.subscribe && topics) {
        this.unsubscribers = [
          events.subscribe(topics.cartUpdate, payload => this.onThemeUpdate(payload)),
          events.subscribe(topics.openCartDrawer, () => this.open()),
        ];
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.unsubscribers?.forEach(unsubscribe => typeof unsubscribe === 'function' && unsubscribe());
      this.unsubscribers = null;
      clearTimeout(this.addTimer);
      if (this.hasAttribute('open')) this.unlock();
    }

    get sectionId() { return this.dataset.sectionId; }
    // At /cart the same element is the page itself: always shown, nothing to open or close.
    get page() { return this.dataset.mode === 'page'; }
    get panel() { return this.querySelector('[data-cart-panel]'); }
    get content() { return this.querySelector('[data-cart-content]'); }
    get rows() { return [...this.querySelectorAll('[data-cart-item]')]; }

    money(pence) {
      return new Intl.NumberFormat(this.dataset.locale || 'en-GB', { style: 'currency', currency: this.dataset.currency || 'GBP' }).format(pence / 100);
    }

    // Opening and closing

    open(opener) {
      if (this.page || this.hasAttribute('open')) return;
      this.opener = opener || document.activeElement;
      this.setAttribute('open', '');
      this.removeAttribute('inert');
      // The page is held still by a class on the root element only. The old theme's own lock also
      // sets overflow on <body>, which un-sticks sticky galleries and makes the page jump.
      document.documentElement.classList.add('egcd-lock');
      // On an unpublished theme Shopify's preview bar sits over the bottom of the window.
      const bar = document.getElementById('PBarNextFrameWrapper'), barHeight = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
      this.style.setProperty('--egcd-bottom', `${barHeight > 0 && barHeight < 120 ? barHeight : 0}px`);
      this.panel?.focus({ preventScroll: true });
      const topic = window.MinimogTheme?.pubSubEvents?.openCartDrawer;
      if (topic) window.MinimogEvents?.emit?.(topic);
    }

    close() {
      if (this.page || !this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.setAttribute('inert', '');
      this.unlock();
      this.showError('');
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }

    unlock() {
      document.documentElement.classList.remove('egcd-lock');
    }

    onPageClick(event) {
      const link = event.target.closest?.('a.m-cart-icon-bubble, [data-open-basket]');
      if (this.page || !link || this.contains(link) || event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(link);
    }

    onKeydown(event) {
      if (event.key === 'Enter' && event.target.matches?.('[data-cart-quantity]')) {
        // Enter in a quantity box updates that line rather than going to checkout.
        event.preventDefault();
        this.typed(event.target);
        return;
      }
      if (this.page) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (event.key !== 'Tab') return;
      const focusable = [...this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])')].filter(node => node.tabIndex >= 0 && node.getClientRects().length > 0);
      if (!focusable.length) { event.preventDefault(); return; }
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    // What the shopper does inside the drawer

    onClick(event) {
      const target = event.target.closest?.('[data-cart-close], [data-cart-step], [data-cart-remove], [data-cart-add], [data-cart-checkout]');
      if (!target) return;
      if (target.matches('[data-cart-close]')) { this.close(); return; }
      if (target.matches('[data-cart-checkout]')) { event.preventDefault(); this.checkout(target); return; }
      if (target.matches('[data-cart-add]')) { this.addOffer(target); return; }
      const row = target.closest('[data-cart-item]');
      if (!row) return;
      if (target.matches('[data-cart-remove]')) { this.setQuantity(row, 0); return; }
      const input = row.querySelector('[data-cart-quantity]');
      const direction = Number(target.dataset.cartStep), step = Number(input.step) || 1, minimum = Number(input.dataset.min) || 1;
      let next = (Number(input.value) || 0) + direction * step;
      if (next < minimum) next = direction < 0 ? 0 : minimum;
      this.setQuantity(row, next);
    }

    onChange(event) {
      if (event.target.matches?.('[data-cart-quantity]')) this.typed(event.target);
      else if (event.target.matches?.('[data-cart-note]')) this.saveNote(event.target.value);
    }

    // The order note is saved as soon as it is written, and checkout waits for it.
    saveNote(note) {
      this.noting = fetch(`${this.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      }).catch(() => {}).finally(() => { this.noting = null; });
    }

    typed(input) {
      const row = input.closest('[data-cart-item]');
      const quantity = Math.floor(Number(input.value));
      if (row) this.setQuantity(row, Number.isFinite(quantity) && quantity >= 0 ? quantity : Number(row.dataset.quantity));
    }

    setQuantity(row, quantity) {
      const input = row.querySelector('[data-cart-quantity]');
      const maximum = input.max === '' ? Infinity : Number(input.max);
      this.showError('');
      if (quantity > maximum) { quantity = maximum; this.showError(this.dataset.limit.replace('[count]', maximum)); }
      input.value = quantity;
      if (quantity === 0) this.leave(row);
      const id = row.dataset.id, waiting = this.ops.find(op => op.row === id);
      const expected = this.sent.has(id) ? this.sent.get(id) : Number(row.dataset.quantity);
      if (waiting) waiting.quantity = quantity;
      else if (quantity !== expected) this.ops.push({ row: id, quantity });
      this.estimate();
      if (this.ops.length) this.flush();
    }

    // A removed row closes up at once; Shopify's reply then takes it out of the markup.
    leave(row) {
      if (row.hasAttribute('data-leaving')) return;
      row.style.height = `${row.offsetHeight}px`;
      void row.offsetHeight; // settle the starting height before it animates to nothing
      row.setAttribute('data-leaving', '');
      row.style.height = '0px';
      if (row.contains(document.activeElement)) this.panel.focus({ preventScroll: true });
    }

    addOffer(button) {
      const card = button.closest('[data-cart-offer]');
      const option = card?.querySelector('[data-cart-offer-variant]')?.selectedOptions[0];
      const id = Number(option?.value || button.dataset.cartAdd);
      if (!id || card.hasAttribute('data-leaving')) return;
      const quantity = Number(option?.dataset.quantity || button.dataset.quantity) || 1;
      const price = Number(option?.dataset.price || card.dataset.price) || 0;
      this.showError('');
      this.showArriving({
        title: card.querySelector('.egcd-offer-title')?.textContent.trim(),
        meta: [option?.dataset.label, this.money(price)].filter(Boolean).join(' · '),
        image: card.querySelector('img')?.currentSrc || card.querySelector('img')?.src,
      });
      card.setAttribute('data-leaving', '');
      card.hidden = true;
      this.arriving.push({ price, quantity });
      this.ops.push({ add: id, quantity });
      this.estimate();
      this.flush();
      const scroller = this.querySelector('[data-cart-scroll]');
      if (scroller) scroller.scrollTo({ top: 0, behavior: 'smooth' });
    }

    async checkout(button) {
      if (button.hasAttribute('data-loading')) return;
      button.setAttribute('data-loading', '');
      // Anything still on its way to Shopify must land before checkout is opened.
      const note = this.querySelector('[data-cart-note]');
      if (note && document.activeElement === note) this.saveNote(note.value);
      while (this.flushing || this.noting) await (this.flushing || this.noting);
      if (this.querySelector('[data-cart-error]:not([hidden])')) { button.removeAttribute('data-loading'); return; }
      window.location.assign(`${this.root}checkout`);
    }

    // Showing the result before Shopify confirms it

    estimate() {
      const content = this.content;
      if (!content) return;
      let count = 0, total = Number(content.dataset.total) || 0, saving = 0;
      for (const row of this.rows) {
        const was = Number(row.dataset.quantity) || 0, line = Number(row.dataset.line) || 0, original = Number(row.dataset.lineOriginal) || 0;
        const now = row.hasAttribute('data-leaving') ? 0 : Number(row.querySelector('[data-cart-quantity]').value) || 0;
        const share = was ? now / was : 1, guess = Math.round(line * share);
        count += now;
        total += guess - line;
        if (row.hasAttribute('data-leaving')) continue;
        saving += Math.max(0, Math.round(original * share) - guess);
        this.write(row.querySelector('[data-cart-line-total]'), this.money(guess));
        this.write(row.querySelector('[data-cart-line-original]'), this.money(Math.round(original * share)));
      }
      for (const item of this.arriving) { count += item.quantity; total += item.price * item.quantity; }
      total = Math.max(0, total);

      const badge = this.querySelector('[data-cart-count]');
      if (badge) { badge.hidden = count === 0; this.write(badge, `(${count})`); }
      this.querySelectorAll('[data-cart-subtotal]').forEach(node => this.write(node, this.money(total)));
      const savingRow = this.querySelector('[data-cart-saving-row]');
      if (savingRow) { savingRow.hidden = saving <= 0; this.write(savingRow.querySelector('[data-cart-saving]'), this.money(saving)); }
      const divisor = Number(this.dataset.vatDivisor);
      if (divisor) this.write(this.querySelector('[data-cart-ex-vat]'), this.money(Math.round(total * 100 / divisor)));

      const freeFrom = Number(this.dataset.freeFrom);
      if (freeFrom > 0) {
        const free = total >= freeFrom;
        this.write(this.querySelector('[data-cart-free-message]'), free ? this.dataset.reached : this.dataset.remaining.replace('[amount]', this.money(freeFrom - total)));
        const progress = this.querySelector('[data-cart-progress]'), percent = Math.min(100, Math.floor(total * 100 / freeFrom));
        if (progress) { progress.setAttribute('aria-valuenow', percent); progress.firstElementChild.style.width = `${percent}%`; }
        // The charge depends on weight, which only Shopify knows; free delivery is certain either way.
        const delivery = this.querySelector('[data-cart-delivery]');
        if (delivery && free) this.write(delivery, this.dataset.deliveryFree);
        else if (delivery && delivery.textContent.trim() === this.dataset.deliveryFree) this.write(delivery, '…');
      }
      this.showCount(count);
    }

    write(node, text) {
      if (!node || node.textContent === text) return;
      node.textContent = text;
      node.classList.remove('is-changed');
      void node.offsetWidth; // restart the highlight
      node.classList.add('is-changed');
    }

    // The header count follows at once too.
    showCount(count) {
      document.querySelectorAll('m-cart-count').forEach((badge) => {
        if (typeof badge.updateCount !== 'function' || badge.itemCount === count) return;
        badge.updateCount(count < 100 ? count : '99+', count);
        badge.classList.remove('egcd-bump');
        void badge.offsetWidth;
        badge.classList.add('egcd-bump');
      });
    }

    showArriving({ title = '', meta = '', image = '' } = {}) {
      const block = this.querySelector('[data-cart-adding]');
      if (!block) return;
      block.querySelector('[data-cart-adding-title]').textContent = title;
      block.querySelector('[data-cart-adding-meta]').textContent = meta;
      const picture = block.querySelector('[data-cart-adding-image]');
      if (image) picture.style.backgroundImage = `url("${image}")`; else picture.removeAttribute('style');
      block.hidden = false;
      block.parentElement.toggleAttribute('data-adding', true);
    }

    hideArriving() {
      const block = this.querySelector('[data-cart-adding]');
      if (!block) return;
      block.hidden = true;
      block.parentElement.removeAttribute('data-adding');
    }

    // Sending changes: one request at a time, later taps on the same line folded into one.

    flush() {
      if (!this.flushing) this.flushing = this.run().finally(() => { this.flushing = null; if (this.ops.length) this.flush(); });
      return this.flushing;
    }

    async run() {
      this.setPending(true);
      let last = null, kind = 'change';
      try {
        do {
          while (this.ops.length) {
            const op = this.ops.shift();
            let result;
            if (op.add) {
              result = await this.post('cart/add.js', { items: [{ id: op.add, quantity: op.quantity }] });
              kind = 'add';
            } else {
              const key = this.keyFor(op.row);
              if (!key) continue;
              this.sent.set(op.row, op.quantity);
              result = await this.post('cart/change.js', { id: key, quantity: op.quantity });
            }
            last = result;
            this.remember(result.sections?.[this.sectionId]);
          }
          if (!this.server) await this.fetchSection();
        } while (this.ops.length);
        this.arriving = [];
        this.apply();
        if (last) { this.announce(this.dataset.updated); this.publish(last, kind); }
      } catch (error) {
        // Put back what Shopify actually holds, then say why the change was refused.
        this.ops = [];
        this.arriving = [];
        this.server = null;
        await this.fetchSection();
        this.apply(true);
        this.showError(error.message || this.dataset.error);
      } finally {
        this.setPending(false);
      }
    }

    async post(path, body) {
      const sections = [];
      this.asking = true;
      document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }));
      this.asking = false;
      const response = await fetch(`${this.root}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ ...body, sections: [...new Set(sections)], sections_url: window.location.pathname }),
      });
      let result;
      try { result = await response.json(); } catch (error) { throw new Error(this.dataset.error); }
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.error);
      return result;
    }

    keyFor(row) {
      const source = this.server || this.content;
      return [...source.querySelectorAll('[data-cart-item]')].find(node => node.dataset.id === row)?.dataset.key;
    }

    remember(html) {
      this.server = html ? new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-content]') : null;
    }

    async fetchSection() {
      try {
        const response = await fetch(`${this.root}${this.page ? 'cart' : ''}?section_id=${encodeURIComponent(this.sectionId)}`, { headers: { Accept: 'text/html' } });
        if (response.ok) this.remember(await response.text());
      } catch (error) {
        console.error('The basket drawer could not be refreshed.', error);
      }
    }

    async refresh() {
      if (this.flushing) return; // the running changes end with Shopify's latest markup anyway
      await this.fetchSection();
      if (!this.flushing) this.apply();
    }

    // Tell the header count, product pages and delivery estimates through the events they already use.
    publish(result, kind) {
      const topic = window.MinimogTheme?.pubSubEvents?.cartUpdate;
      if (topic) window.MinimogEvents?.emit?.(topic, { cart: result, source: this });
      const name = kind === 'add' ? 'product-ajax:added' : 'cart:updated';
      const detail = kind === 'add' ? { product: result, source: this } : { cart: result, source: this };
      document.dispatchEvent(new CustomEvent(name, { detail }));
    }

    // Adds made elsewhere on the page

    beginAdd() {
      if (this.page) return;
      this.adding = true;
      this.showArriving(this.describeSubmitted());
      this.open();
      clearTimeout(this.addTimer);
      this.addTimer = setTimeout(() => this.cancelAdd(), 10000);
    }

    // Name and photo of what was just submitted, read from the product page or add-on card it sits in.
    describeSubmitted() {
      const { form, at } = this.submitted || {};
      this.submitted = null;
      if (!form?.isConnected || Date.now() - at > 2000) return {};
      let scope = form.parentElement;
      while (scope && scope !== document.body && !scope.querySelector('h1, h3')) scope = scope.parentElement;
      const heading = scope?.querySelector('h1, h3');
      if (!heading || (heading.localName === 'h1' && !scope.closest('.eg3-product, .egf'))) return {};
      const area = heading.localName === 'h1' ? scope.closest('.eg3-product, .egf') : scope;
      const image = [...area.querySelectorAll('img')].find(candidate => candidate.currentSrc || candidate.getAttribute('src'));
      return { title: heading.textContent.trim(), image: image?.currentSrc || image?.src };
    }

    cancelAdd() {
      clearTimeout(this.addTimer);
      if (!this.adding) return;
      this.adding = false;
      this.hideArriving();
      this.close(); // the product page shows why it could not be added
    }

    onThemeUpdate(payload) {
      if (payload?.source === this) return;
      const html = payload?.cart?.sections?.[this.sectionId];
      if (!html) { this.refresh(); return; }
      this.remember(html);
      if (!this.flushing) this.apply();
      // A product form emits this just before its "added" event, which then finds the drawer up to date.
      this.fresh = true;
      setTimeout(() => { this.fresh = false; }, 0);
    }

    onAdded(event) {
      if (event.detail?.source === this) return;
      clearTimeout(this.addTimer);
      this.adding = false;
      if (!this.fresh) this.refresh();
      this.fresh = false;
      this.open();
    }

    // Merging Shopify's markup into what is on screen

    apply(replace = false) {
      const next = this.server, current = this.content;
      if (!next || !current) return;
      this.sent.clear();
      const inside = this.contains(document.activeElement), focused = inside ? document.activeElement.dataset.focus : null;
      // Between an empty and a filled basket the layout differs too much to merge.
      if (replace || !current.querySelector('[data-cart-items]') !== !next.querySelector('[data-cart-items]')) {
        const scrollTop = this.querySelector('[data-cart-scroll]')?.scrollTop || 0;
        const kept = current.querySelector('[data-cart-keep]');
        current.replaceWith(document.importNode(next, true));
        if (kept) this.querySelector('[data-cart-keep]')?.replaceWith(kept);
        const scroller = this.querySelector('[data-cart-scroll]');
        if (scroller) scroller.scrollTop = scrollTop;
      } else {
        this.morph(current, next);
      }
      if (!(this.page || this.hasAttribute('open')) || !inside || this.contains(document.activeElement)) return;
      const again = focused && [...this.querySelectorAll('[data-focus]')].find(node => node.dataset.focus === focused);
      (again || this.panel).focus({ preventScroll: true });
    }

    morph(from, to) {
      // Shopify's own scripts build the express checkout buttons; they are left as they are.
      if (from.hasAttribute('data-cart-keep') || from.localName === 'textarea') return;
      for (const { name, value } of [...to.attributes]) if (from.getAttribute(name) !== value) from.setAttribute(name, value);
      for (const { name } of [...from.attributes]) if (!to.hasAttribute(name)) from.removeAttribute(name);
      if (from.localName === 'input') { from.value = to.getAttribute('value') ?? ''; return; }
      if (to.hasAttribute('data-cart-keyed')) { this.morphKeyed(from, to); return; }
      const before = from.localName === 'select' ? [from.value, [...from.options].map(option => option.value).join()] : null;
      const a = [...from.childNodes], b = [...to.childNodes];
      if (a.length === b.length && a.every((node, index) => node.nodeType === b[index].nodeType && node.nodeName === b[index].nodeName)) {
        a.forEach((node, index) => {
          if (node.nodeType === 1) this.morph(node, b[index]);
          else if (node.nodeValue !== b[index].nodeValue) node.nodeValue = b[index].nodeValue;
        });
      } else {
        from.replaceChildren(...b.map(node => document.importNode(node, true)));
      }
      // Keep the option the shopper picked when the choices are unchanged.
      if (before) from.value = before[1] === [...from.options].map(option => option.value).join() ? before[0] : (to.querySelector('option[selected]') || to.options[0])?.value;
    }

    // Rows and add-on cards are matched by id, so existing ones stay in place and new ones slide in.
    morphKeyed(from, to) {
      const have = new Map([...from.children].map(node => [node.dataset.id, node]));
      let previous = null;
      for (const target of [...to.children]) {
        let node = have.get(target.dataset.id);
        if (node) {
          have.delete(target.dataset.id);
          this.morph(node, target);
        } else {
          node = document.importNode(target, true);
          node.classList.add('is-entering');
        }
        const expected = previous ? previous.nextElementSibling : from.firstElementChild;
        if (node !== expected) from.insertBefore(node, expected);
        previous = node;
      }
      have.forEach(node => node.remove());
    }

    setPending(pending) {
      this.content?.toggleAttribute('data-pending', pending);
    }

    announce(message) {
      const status = this.querySelector('[data-cart-status]');
      if (status) status.textContent = message || '';
    }

    showError(message) {
      const error = this.querySelector('[data-cart-error]');
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
    }
  });
}
